import {observer} from 'mobx-react-lite';
import {AdvancedSearchDialogProps} from '../../../../entity/props/title-template/advanced-search-dialog-props';
import {Box,
  Button, Dialog,
  DialogContent,
  DialogTitle, Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select, TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid2';
import React, {useEffect, useState} from 'react';
import {Form, Formik, FormikProps} from 'formik';
import {DropdownOption} from '../../../../entity/core/dropdown-option';
import {useService} from '../../../../infrastructure/service/service';
import {State} from '../../../../entity/model/state';
import {AdvancedSearchDto} from '../../../../entity/dto/advanced-search-dto';
import {County} from '../../../../entity/model/county';

import '../../../../assets/css/style.css';

const AdvancedSearchDialog = (props: AdvancedSearchDialogProps): JSX.Element => {
  const {titleTemplateService, stateService, countyService} = useService();

  const [stateOptions, setStateOptions] = useState<DropdownOption[]>([]);
  const [countyOptions, setCountyOptions] = useState<DropdownOption[]>([]);
  const [counties, setCounties] = useState<County[]>([]);

  useEffect(() => {
    const init = async () => {
      const states = await stateService.getStates();
      setStateOptions(states.map((state: State): DropdownOption => ({key: state.id, text: state.shortName, value: state.id})));

      const counties = await countyService.getCounties();
      setCounties(counties);
      setCountyOptions(counties.sort((a: County, b: County): number => a.name.localeCompare(b.name)).map((county: County): DropdownOption => ({key: county.id, text: county.name, value: county.id})));
    }

    init();
  }, []);

  const handleCancel = (): void => {
    props.setOpen(false);
    setCountyOptions(counties.sort((a: County, b: County): number => a.name.localeCompare(b.name)).map((county: County): DropdownOption => ({key: county.id, text: county.name, value: county.id})));
  }

  const handleStateChange = (handleChange: any) => (event: any): void => {
    let stateCounties = counties.filter((county: County): boolean => county.stateID === event.target.value).sort((a: County, b: County): number => a.name.localeCompare(b.name));
    setCountyOptions(stateCounties.map((county: County): DropdownOption => ({key: county.id, text: county.name, value: county.id})));

    handleChange(event);
  }

  return (
    <Dialog open={props.open} transitionDuration={600} >
      <DialogTitle className={'zitles-dialog-title'}>
        <span>Filter Search</span>
        <IconButton id={'btn-advanced-search-close'} onClick={handleCancel}>
          <CloseIcon className={'dialog-icon'} />
        </IconButton>
      </DialogTitle>
      <DialogContent className={'zitles-dialog-content'}>
        <Formik
          initialValues={titleTemplateService.getAdvancedSearchDto()}
          onSubmit={props.handleSubmit}>
          {({values, handleSubmit, handleChange}: FormikProps<AdvancedSearchDto>): JSX.Element => (
            <Form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <FormControl fullWidth>
                    <InputLabel>State</InputLabel>
                    <Select
                      id={'select-advanced-search-state'}
                      label='State'
                      name={'state'}
                      value={values.state}
                      onChange={handleStateChange(handleChange)}>
                      {
                        stateOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                      }
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={6}>
                  <FormControl fullWidth>
                    <InputLabel>County</InputLabel>
                    <Select
                      id={'select-advanced-search-county'}
                      label='County'
                      name={'county'}
                      value={values.county}
                      onChange={handleChange}>
                      {
                        countyOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                      }
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <TextField
                    id={'text-advanced-search-template-name'}
                    fullWidth
                    name={'templateName'}
                    value={values.templateName}
                    label='Order or Template'
                    onChange={handleChange}/>
                </Grid>
                <Grid size={12}>
                  <TextField
                    id={'text-advanced-search-tms-numbers'}
                    fullWidth
                    name={'tmsNumbers'}
                    value={values.tmsNumbers}
                    label='Parcel Pin / TMS'
                    onChange={handleChange}/>
                </Grid>
                <Grid size={12}>
                  <TextField
                    id={'text-advanced-search-book-page'}
                    fullWidth
                    name={'bookAndPage'}
                    value={values.bookAndPage}
                    label='Instrument (Book and Page)'
                    onChange={handleChange}/>
                </Grid>
                <Grid size={12}>
                  <TextField
                    id={'text-advanced-search-address'}
                    fullWidth
                    name={'address'}
                    value={values.address}
                    label='Street Address'
                    onChange={handleChange}/>
                </Grid>
                <Grid size={12}>
                  <TextField
                    id={'text-advanced-search-subdivision'}
                    fullWidth
                    name={'subdivision'}
                    value={values.subdivision}
                    label='Subdivision'
                    onChange={handleChange}/>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2, mx: -3 }} />
              <Box className={'action-btn-container'}>
                <Button
                  id={'btn-advanced-search-cancel'}
                  disableTouchRipple
                  onClick={handleCancel}
                  className={'zitles-button cancel'}>
                  Cancel
                </Button>
                <Button
                  id={'btn-advanced-search-submit'}
                  type='submit'
                  className={'zitles-button submit'}>
                  Apply Filter
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export default observer(AdvancedSearchDialog);
